import { axiosInstance } from "api/axios";
import pluginService from "api/pluginService";
import { IKey } from "dto/IKey";
import { IOutgoingPrintLetter } from "dto/Printing/IOutgoingPrint";
import { blobToBase64 } from "utils/blobToBase64";
import { decryptFile } from "utils/decryptFile";
import { getBlob } from "utils/getBlob";
import { readFileFromBase64 } from "utils/readFileFromBase64";

/**
 * PDF pisma do wydruku: pobranie pliku, odszyfrowanie kluczem obiektu (model E2E — klucze
 * przychodzą w odpowiedzi źródeł wydruku) i — dla dokumentu źródłowego (docx) — konwersja
 * do PDF wtyczką. Samodzielny odpowiednik usePDF.decryptPackage do pętli wydruku: nie czeka
 * na przerenderowanie KeysContext i każdy błąd kończy się wyjątkiem (nie wiszącą obietnicą).
 */
export const loadLetterPdf = async (
  letter: IOutgoingPrintLetter,
  keys: IKey[],
  userId: string
): Promise<Blob> => {
  const { data } = await axiosInstance.get<{ Url: string }>(
    `/file?id=${encodeURIComponent(letter.Uri)}&ownerKind=${letter.Kind}`
  );
  if (!data?.Url) throw new Error("Nie odnaleziono pliku pisma.");

  const response = await getBlob(data.Url);
  let blob = new Blob([response.data]);

  if (letter.EncryptionKeyId) {
    const key = keys.find((k) => k.Id === letter.EncryptionKeyId);
    if (!key) throw new Error("Brak klucza do odszyfrowania pisma.");
    const parent = key.ParentKeyId ? keys.find((k) => k.Id === key.ParentKeyId) : null;
    blob = await decryptFile(userId, blob, key, parent);
  }

  if (letter.IsPdf) return new Blob([blob], { type: "application/pdf" });

  // Dokument źródłowy (docx) — konwersja wtyczką, jak podgląd dokumentu.
  const base64 = (await blobToBase64(blob)) as string;
  const fileName = /\.[a-z0-9]{2,5}$/i.test(letter.FileName)
    ? letter.FileName
    : `${letter.FileName}.docx`;
  const converted = await pluginService.preview({
    AttachmentId: letter.Uri,
    DocumentId: letter.DocumentId ?? undefined,
    Content: base64.split(",")[1],
    FileName: fileName,
    Kind: letter.Kind,
  });
  const buffer = await readFileFromBase64(converted.data.Content, "application/pdf");
  return new Blob([buffer], { type: "application/pdf" });
};
